// Puerto de la nomina del proveedor (PP-22). La vista habla con esto y no con
// fetch: en los tests se reemplaza por un doble y en la app por http.ts.
//
// Todo metodo recibe la planta seleccionada (siteId y providerId de la
// habilitacion, ADR-015). El portal no tiene un tenant global: la planta viaja
// en cada llamada.
import type {
  CreateDriverRequest,
  CreateVehicleRequest,
  Driver,
  ProviderRosterResponse,
  SiteVehicle,
  Subcontractor,
  UpdateDriverRequest,
  UpdateVehicleRequest,
} from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import type { SiteAccess } from "../sites/port.js";
import type { DocumentsPort } from "./documents/port.js";

/** La planta y el proveedor con el que la empresa esta habilitada en ella. */
export type FleetSite = Pick<SiteAccess, "siteId" | "providerId">;

export interface Roster<T> {
  /** Fecha operativa de la planta con la que la API calculo la elegibilidad. */
  readonly today: string;
  readonly items: readonly T[];
}

export type VehicleDraftRequest = Omit<CreateVehicleRequest, "providerId">;
export type DriverDraftRequest = Omit<CreateDriverRequest, "providerId">;

/** La nomina y sus documentos (PP-23): un solo puerto para que el shell no cablee otro. */
export interface FleetPort extends DocumentsPort {
  listVehicles(session: PortalSession, site: FleetSite): Promise<Roster<SiteVehicle>>;
  listDrivers(session: PortalSession, site: FleetSite): Promise<Roster<Driver>>;
  listSubcontractors(session: PortalSession, site: FleetSite): Promise<readonly Subcontractor[]>;
  createVehicle(session: PortalSession, site: FleetSite, body: VehicleDraftRequest): Promise<SiteVehicle>;
  updateVehicle(session: PortalSession, site: FleetSite, vehicleId: string, body: UpdateVehicleRequest): Promise<SiteVehicle>;
  deactivateVehicle(session: PortalSession, site: FleetSite, vehicleId: string): Promise<SiteVehicle>;
  /** Reincorporacion a la nomina (inRoster vuelve a true), simetrica de la baja. */
  reinstateVehicle(session: PortalSession, site: FleetSite, vehicleId: string): Promise<SiteVehicle>;
  createDriver(session: PortalSession, site: FleetSite, body: DriverDraftRequest): Promise<Driver>;
  updateDriver(session: PortalSession, site: FleetSite, driverId: string, body: UpdateDriverRequest): Promise<Driver>;
  deactivateDriver(session: PortalSession, site: FleetSite, driverId: string): Promise<Driver>;
  reinstateDriver(session: PortalSession, site: FleetSite, driverId: string): Promise<Driver>;
  /**
   * Nomina de todas las plantas (PP-70): cada integrante una vez, con su
   * elegibilidad en cada planta. Sin planta: la API la resuelve de la cuenta.
   */
  listRoster(session: PortalSession): Promise<ProviderRosterResponse>;
}

/**
 * Por que fallo una llamada, sin el cuerpo remoto: la UI elige el copy por
 * este codigo y nunca pinta el `detail` de la API.
 * - access: 401 o 403 (la sesion no puede operar esta nomina).
 * - not_found: 404 (fuera del alcance o ya no existe).
 * - conflict: 409 generico (patente o codigo de movil repetidos, ya estaba
 *   en la nomina, carrera).
 * - subcontract_missing: 409 del titular (tercerizada inactiva o ajena), en
 *   el alta y en la reincorporacion.
 * - validation: 400 (datos que la API rechaza).
 * - unavailable: 5xx, red o respuesta que viola el contrato.
 */
export type FleetErrorKind = "access" | "not_found" | "conflict" | "subcontract_missing" | "validation" | "unavailable";

export class FleetError extends Error {
  constructor(readonly kind: FleetErrorKind) {
    super(`fleet_${kind}`);
  }
}
